// ===========================================
// WORKSPACE CONTABLE: Tipos de Faceta
// ===========================================

// ===========================================
// CONDICIONES IVA
// ===========================================

export type CondicionIVA =
  | 'responsable_inscripto'
  | 'monotributo'
  | 'exento'
  | 'consumidor_final'
  | 'no_responsable'
  | 'monotributo_social'

export const CONDICIONES_IVA: Record<CondicionIVA, string> = {
  responsable_inscripto: 'Responsable Inscripto',
  monotributo: 'Monotributo',
  exento: 'Exento',
  consumidor_final: 'Consumidor Final',
  no_responsable: 'No Responsable',
  monotributo_social: 'Monotributo Social',
}

// ===========================================
// CATEGORIAS MONOTRIBUTO
// ===========================================

export const CATEGORIAS_MONOTRIBUTO = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'] as const
export type CategoriaMonotributo = typeof CATEGORIAS_MONOTRIBUTO[number]

// ===========================================
// IMPUESTOS
// ===========================================

export const TIPOS_IMPUESTO = [
  'iva',
  'ganancias',
  'ganancias_anticipo',
  'bienes_personales',
  'iibb',
  'monotributo',
  'autonomos',
  'suss',
  'retenciones',
  'siradig',
] as const

export type TipoImpuesto = typeof TIPOS_IMPUESTO[number]

export const IMPUESTOS_LABELS: Record<TipoImpuesto, string> = {
  iva: 'IVA',
  ganancias: 'Ganancias',
  ganancias_anticipo: 'Ganancias (Anticipos)',
  bienes_personales: 'Bienes Personales',
  iibb: 'Ingresos Brutos',
  monotributo: 'Monotributo',
  autonomos: 'Autonomos',
  suss: 'SUSS',
  retenciones: 'Retenciones',
  siradig: 'SIRADIG',
}

// ===========================================
// FACETA CONTABLE
// ===========================================

export interface FacetaContable {
  id: string
  cliente_id: string

  // Datos fiscales
  condicion_iva: CondicionIVA | null
  categoria_monotributo: CategoriaMonotributo | null

  // Actividades
  actividad_principal: string | null
  actividades_secundarias: string[]
  codigo_actividad_afip: string | null

  // Jurisdicciones
  provincia_principal: string | null
  provincias_iibb: string[]

  // Cierre y ejercicio
  cierre_ejercicio: string | null  // "12" para diciembre
  fecha_inicio_actividades: string | null

  // Obligaciones
  impuestos_inscriptos: TipoImpuesto[]
  presenta_ddjj_iva: boolean
  presenta_ddjj_ganancias: boolean
  tiene_empleados: boolean

  // Honorarios
  honorarios_mensuales: number
  honorarios_desde: string | null

  // Facturacion
  factura_por_sistema: boolean
  punto_venta_asignado: number | null

  // Claves
  clave_fiscal: string | null
  otras_claves: OtraClave[]

  // Estado
  activo: boolean

  created_at: string
  updated_at: string
}

export interface OtraClave {
  nombre: string
  clave: string
  usuario?: string
}

// ===========================================
// FORMULARIOS
// ===========================================

export interface FacetaContableInsert {
  cliente_id: string
  condicion_iva?: CondicionIVA
  categoria_monotributo?: CategoriaMonotributo
  actividad_principal?: string
  actividades_secundarias?: string[]
  codigo_actividad_afip?: string
  provincia_principal?: string
  provincias_iibb?: string[]
  cierre_ejercicio?: string
  fecha_inicio_actividades?: string
  impuestos_inscriptos?: TipoImpuesto[]
  presenta_ddjj_iva?: boolean
  presenta_ddjj_ganancias?: boolean
  tiene_empleados?: boolean
  honorarios_mensuales?: number
  honorarios_desde?: string
  factura_por_sistema?: boolean
  punto_venta_asignado?: number
  clave_fiscal?: string
  otras_claves?: OtraClave[]
}

export type FacetaContableUpdate = Partial<Omit<FacetaContableInsert, 'cliente_id'>>

// ===========================================
// VISTA COMBINADA (Cliente + Persona + Faceta)
// ===========================================

export interface ClienteContableCompleto {
  // Del cliente
  id: string
  organizacion_id: string
  persona_id: string
  codigo_cliente: string | null
  estado: string
  fecha_alta: string
  fecha_baja: string | null
  responsable_id: string | null
  workspaces: string[]
  descripcion: string | null

  // De la persona
  persona_tipo: 'fisica' | 'juridica'
  nombre: string | null
  apellido: string | null
  razon_social: string | null
  nombre_completo: string
  cuit: string | null
  email: string | null
  telefono: string | null
  domicilio_provincia: string | null
  domicilio_localidad: string | null

  // De la faceta contable
  condicion_iva: CondicionIVA | null
  honorarios_mensuales: number | null
  tiene_empleados: boolean | null
  impuestos_inscriptos: TipoImpuesto[] | null

  // Calculados
  saldo?: number
  ultima_actividad?: string
}

// ===========================================
// PROVINCIAS ARGENTINAS
// ===========================================

export const PROVINCIAS_ARGENTINA = [
  'Buenos Aires',
  'CABA',
  'Catamarca',
  'Chaco',
  'Chubut',
  'Cordoba',
  'Corrientes',
  'Entre Rios',
  'Formosa',
  'Jujuy',
  'La Pampa',
  'La Rioja',
  'Mendoza',
  'Misiones',
  'Neuquen',
  'Rio Negro',
  'Salta',
  'San Juan',
  'San Luis',
  'Santa Cruz',
  'Santa Fe',
  'Santiago del Estero',
  'Tierra del Fuego',
  'Tucuman',
] as const

export type ProvinciaArgentina = typeof PROVINCIAS_ARGENTINA[number]
